import { useState, useRef, useEffect } from "react";
import { callAiChatStreaming } from "../../lib/aiCall";
import { loadChatHistory, saveChatHistory, clearChatHistory, type ChatMessage } from "../../lib/storage";

// Local alias — Message is the canonical name used throughout this file.
type Message = ChatMessage;

// Cap on how many prior messages we replay to Claude in each request. Beyond
// this, the system prompt + portfolio context already provides the heavy lift
// and adding more conversational history just consumes input budget.
const CHAT_SEND_HISTORY_CAP = 20;

interface AIChatDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  portfolioContext: string;
  currentPage: string;
  onUpgradeRequired?: () => void;
}

const SUGGESTIONS: Record<string, string[]> = {
  strategy: ["What should I focus on this year?", "Am I on track for my goals?", "What is my biggest risk right now?"],
  portfolio: ["Which property is performing best?", "Should I sell any properties?", "How can I improve my cashflow?"],
  growth: ["When will my portfolio reach $5m?", "Which property has the best growth outlook?", "Should I sell at peak growth?"],
  loans: ["Which loan should I pay down first?", "Should I refinance?", "How long until I can access equity?"],
  simulator: ["Model a sale for me", "Help me analyse Keep vs Re-lease", "What would buying another property do to my portfolio?"],
};

const DEFAULT_SUGGESTIONS = ["Summarise my portfolio position", "What are my next steps?", "Explain my tax situation"];

function getSuggestions(page: string): string[] {
  return SUGGESTIONS[page] ?? DEFAULT_SUGGESTIONS;
}

function LoadingDots() {
  return (
    <div className="mr-8 flex gap-1 rounded-2xl rounded-bl-sm border border-neutral-200 bg-neutral-50 px-4 py-3">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="inline-block h-2 w-2 rounded-full bg-neutral-400 animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  );
}

export default function AIChatDrawer({
  isOpen,
  onClose,
  portfolioContext,
  currentPage,
  onUpgradeRequired,
}: AIChatDrawerProps) {
  // Lazy initializer reads persisted history from localStorage on first
  // mount, pruned to the 7-day retention window. See lib/storage.ts.
  const [messages, setMessages] = useState<Message[]>(() => loadChatHistory());
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  // Persist on every change. saveChatHistory caps at 50 messages internally.
  useEffect(() => {
    saveChatHistory(messages);
  }, [messages]);

  async function sendMessage(userText: string) {
    if (!userText.trim() || isLoading) return;

    const now = Date.now();
    const newMessages: Message[] = [
      ...messages,
      { id: now.toString(), role: "user" as const, content: userText.trim(), createdAt: now },
    ];
    setMessages(newMessages);
    setInputValue("");
    setIsLoading(true);
    setError(null);

    const assistantId = (now + 1).toString();
    setMessages((m) => [...m, { id: assistantId, role: "assistant" as const, content: "", createdAt: now + 1 }]);

    try {
      const systemPrompt = `You are a knowledgeable Australian property investment assistant. You help investors understand their portfolio, model scenarios, and make informed decisions. You are not a licensed financial adviser and always remind users to seek professional advice for major decisions.\n\nCurrent portfolio data:\n\n${portfolioContext}`;
      const body = await callAiChatStreaming({
        systemPrompt,
        // Send only the last N messages to Claude — capped to keep input
        // budget reasonable. The system prompt + portfolio context already
        // carries the heavy lift.
        messages: newMessages.slice(-CHAT_SEND_HISTORY_CAP).map((m) => ({ role: m.role, content: m.content })),
      });

      const reader = body.getReader();
      const decoder = new TextDecoder();
      if (!reader) throw new Error("No response body");

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const lines = chunk.split("\n").filter((l) => l.startsWith("data: "));
        for (const line of lines) {
          const data = line.slice(6);
          if (data === "[DONE]") continue;
          try {
            const parsed = JSON.parse(data) as {
              delta?: { text?: string; content?: { text?: string }[] };
            };
            const delta =
              parsed?.delta?.text ??
              parsed?.delta?.content?.[0]?.text ??
              "";
            if (delta) {
              setMessages((m) =>
                m.map((msg) =>
                  msg.id === assistantId
                    ? { ...msg, content: msg.content + delta }
                    : msg
                )
              );
            }
          } catch {
            // skip malformed SSE lines
          }
        }
      }
    } catch (err) {
      setMessages((m) => m.filter((msg) => msg.id !== assistantId));
      if (err instanceof Error && err.message === "upgrade_required") {
        onUpgradeRequired?.();
      } else {
        setError(err instanceof Error ? err.message : "Something went wrong");
      }
    } finally {
      setIsLoading(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  }

  const suggestions = getSuggestions(currentPage);

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20"
          onClick={onClose}
        />
      )}

      {/* Drawer panel */}
      <div
        className={`fixed right-0 top-0 z-50 flex h-full w-[420px] max-w-full flex-col bg-white shadow-2xl transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-semibold text-neutral-900">Portfolio Assistant</span>
            <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-500">
              Viewing: {currentPage}
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {messages.length > 0 && (
              confirmClear ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-neutral-500">Clear?</span>
                  <button
                    onClick={() => {
                      setMessages([]);
                      clearChatHistory();
                      setConfirmClear(false);
                    }}
                    className="rounded-md bg-red-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-red-700"
                  >
                    Yes
                  </button>
                  <button
                    onClick={() => setConfirmClear(false)}
                    className="text-xs text-neutral-400 hover:text-neutral-600"
                  >
                    No
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmClear(true)}
                  className="text-xs text-neutral-400 hover:text-neutral-700"
                >
                  Clear chat
                </button>
              )
            )}
            <button
              onClick={onClose}
              className="rounded-lg p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 transition-colors"
              aria-label="Close"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && !isLoading && (
            <div className="flex flex-col items-center justify-center h-full gap-3 py-8">
              <p className="text-xs text-neutral-400 mb-1">Suggested questions</p>
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => sendMessage(s)}
                  className="w-full rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-2.5 text-left text-sm text-neutral-700 hover:bg-neutral-100 hover:border-neutral-300 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {messages.map((msg) => (
            <div key={msg.id}>
              {msg.role === "user" ? (
                <div className="ml-8 rounded-2xl rounded-br-sm bg-neutral-900 px-4 py-2.5 text-sm text-white">
                  {msg.content}
                </div>
              ) : (
                <div className="mr-8 rounded-2xl rounded-bl-sm border border-neutral-200 bg-neutral-50 px-4 py-2.5 text-sm text-neutral-800 whitespace-pre-wrap">
                  {msg.content || <span className="text-neutral-400 italic">Thinking…</span>}
                </div>
              )}
            </div>
          ))}

          {isLoading && messages[messages.length - 1]?.role !== "assistant" && (
            <LoadingDots />
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Error */}
        {error && (
          <div className="mx-3 mb-1 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-600">
            {error}
          </div>
        )}

        {/* Input area */}
        <div className="border-t p-3 flex gap-2">
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about your portfolio…"
            rows={1}
            className="flex-1 resize-none rounded-xl border border-neutral-200 px-3 py-2 text-sm max-h-32 focus:ring-2 focus:ring-neutral-300 focus:outline-none"
            style={{ minHeight: "2.5rem" }}
          />
          <button
            onClick={() => sendMessage(inputValue)}
            disabled={isLoading || !inputValue.trim()}
            className="rounded-xl bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Send
          </button>
        </div>
      </div>
    </>
  );
}
